import React, { useEffect, useState } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, ActivityIndicator, Platform, Switch } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { colors, spacing, typography } from '../../theme';
import { withAlpha } from '../../theme';
import { apiService } from '../../services/api';
import { useAppContext } from '../../contexts/AppContext';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { useLocale } from '../../contexts/LocaleContext';
import { useRTLStyles } from '../../hooks/useRTLStyles';
import { PushDiagnosticsCard } from './PushDiagnosticsCard';
import { GROUP_ICON, NotificationGroup, notificationVisual } from '../../config/notificationVisuals';

interface TypePref { key: string; push: boolean; inApp: boolean; defaults: { push: boolean; inApp: boolean }; scoped: boolean }
interface Prefs {
  muted: boolean;
  scope: 'all' | 'unassigned' | 'mine';
  isAdmin: boolean;
  groups: Array<{ group: NotificationGroup; types: TypePref[] }>;
}

/**
 * Settings → Notifications: what this person receives and how — pause
 * everything, which conversations (owners/admins), and per notification
 * type a phone/desktop push and an in-app alert. Each change saves at once.
 * Only types that fit their role and the plan are listed (server decides).
 */
export const NotificationSettings: React.FC<{ onBack?: () => void }> = () => {
  const styles = useThemedStyles(getStyles);
  const { t } = useLocale();
  const rtl = useRTLStyles();
  const insets = useSafeAreaInsets();
  const { showNotification } = useAppContext();
  const [prefs, setPrefs] = useState<Prefs | null>(null);
  const [error, setError] = useState(false);
  const [testing, setTesting] = useState(false);
  const [open, setOpen] = useState<Record<string, boolean>>({ messages: true });

  useEffect(() => {
    apiService.authenticatedRequest<Prefs>('/api/notifications/preferences', { method: 'GET' })
      .then((res: any) => (res.statusCode === 1000 && res.data ? setPrefs(res.data) : setError(true)))
      .catch(() => setError(true));
  }, []);

  const save = async (body: Record<string, any>, optimistic: (p: Prefs) => Prefs) => {
    if (!prefs) return;
    const before = prefs;
    setPrefs(optimistic(prefs));
    try {
      const res: any = await apiService.authenticatedRequest('/api/notifications/preferences', { method: 'PUT', body: JSON.stringify(body) });
      if (res.statusCode !== 1000) throw new Error();
    } catch {
      setPrefs(before);
      showNotification('error', t('notificationSettings.saveFailed'), '');
    }
  };

  const setType = (key: string, channel: 'push' | 'inApp', value: boolean) =>
    save({ types: { [key]: { [channel]: value } } }, (p) => ({
      ...p,
      groups: p.groups.map((g) => ({ ...g, types: g.types.map((tp) => (tp.key === key ? { ...tp, [channel]: value } : tp)) })),
    }));

  const setGroup = (group: NotificationGroup, value: boolean) => {
    const g = prefs?.groups.find((x) => x.group === group);
    if (!g) return;
    const types = Object.fromEntries(g.types.map((tp) => [tp.key, { push: value, inApp: value }]));
    save({ types }, (p) => ({
      ...p,
      groups: p.groups.map((x) => (x.group === group ? { ...x, types: x.types.map((tp) => ({ ...tp, push: value, inApp: value })) } : x)),
    }));
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const res: any = await apiService.authenticatedRequest('/api/notifications/preferences/test', { method: 'POST' });
      if (res.statusCode === 1000) showNotification('success', t('notificationSettings.testSent'), t('notificationSettings.testSentHint'));
      else showNotification('error', t('notificationSettings.testFailed'), '');
    } catch {
      showNotification('error', t('notificationSettings.testFailed'), '');
    } finally {
      setTesting(false);
    }
  };

  const bottomPad = Platform.OS !== 'web' ? 50 + spacing.xl * 2 + insets.bottom : spacing.xl;

  if (!prefs) {
    return (
      <View style={styles.center}>
        {error ? <Text style={styles.muted}>{t('notificationSettings.loadFailed')}</Text> : <ActivityIndicator color={colors.primary as string} />}
      </View>
    );
  }

  const row = (title: string, description: string | undefined, right: React.ReactNode, key?: string) => (
    <View key={key} style={[styles.row, { flexDirection: rtl.rowDirection }]}>
      <View style={styles.rowText}>
        <Text style={[styles.rowTitle, { textAlign: rtl.textStart }]}>{title}</Text>
        {!!description && <Text style={[styles.rowDescription, { textAlign: rtl.textStart }]}>{description}</Text>}
      </View>
      {right}
    </View>
  );

  const toggle = (value: boolean, onChange: (v: boolean) => void, label: string, disabled?: boolean) => (
    <View style={styles.toggleCell}>
      <Switch
        value={value}
        onValueChange={onChange}
        disabled={disabled}
        trackColor={{ true: colors.primary as string, false: colors.border as string }}
        thumbColor={Platform.OS === 'android' ? '#FFFFFF' : undefined}
        accessibilityLabel={label}
      />
    </View>
  );

  return (
    <ScrollView style={styles.container} contentContainerStyle={[styles.content, { paddingBottom: bottomPad }]} showsVerticalScrollIndicator={false}>
      <Text style={[styles.title, { textAlign: rtl.textStart }]}>{t('notificationSettings.title')}</Text>
      <Text style={[styles.subtitle, { textAlign: rtl.textStart }]}>{t('notificationSettings.subtitle')}</Text>

      <PushDiagnosticsCard />

      <View style={styles.card}>
        {row(
          t('notificationSettings.pauseAll'),
          t('notificationSettings.pauseAllHint'),
          toggle(prefs.muted, (v) => save({ muted: v }, (p) => ({ ...p, muted: v })), t('notificationSettings.pauseAll')),
        )}
        <TouchableOpacity style={[styles.testRow, { flexDirection: rtl.rowDirection }]} onPress={sendTest} disabled={testing}>
          {testing ? <ActivityIndicator size="small" color={colors.primary as string} /> : <Ionicons name="paper-plane-outline" size={16} color={colors.primary as string} />}
          <Text style={styles.testText}>{t('notificationSettings.sendTest')}</Text>
        </TouchableOpacity>
      </View>

      {prefs.isAdmin && (
        <View style={[styles.card, prefs.muted && styles.dimmed]}>
          <Text style={[styles.cardTitle, { textAlign: rtl.textStart }]}>{t('notificationSettings.scope.title')}</Text>
          <Text style={[styles.rowDescription, { textAlign: rtl.textStart, marginBottom: spacing.sm }]}>{t('notificationSettings.scope.hint')}</Text>
          {(['unassigned', 'all', 'mine'] as const).map((scope) => {
            const active = prefs.scope === scope;
            return (
              <TouchableOpacity
                key={scope}
                style={[styles.option, active && styles.optionActive, { flexDirection: rtl.rowDirection }]}
                onPress={() => save({ scope }, (p) => ({ ...p, scope }))}
                disabled={prefs.muted}
                accessibilityRole="radio"
                accessibilityState={{ checked: active }}
              >
                <Ionicons name={active ? 'radio-button-on' : 'radio-button-off'} size={18} color={(active ? colors.primary : colors.text.secondary) as string} />
                <View style={styles.rowText}>
                  <Text style={[styles.rowTitle, { textAlign: rtl.textStart }]}>{t(`notificationSettings.scope.${scope}` as any)}</Text>
                  <Text style={[styles.rowDescription, { textAlign: rtl.textStart }]}>{t(`notificationSettings.scope.${scope}Hint` as any)}</Text>
                </View>
              </TouchableOpacity>
            );
          })}
        </View>
      )}

      <View style={[styles.columnsHeader, { flexDirection: rtl.rowDirection }]}>
        <View style={styles.rowText} />
        <Text style={styles.columnLabel}>{t('notificationSettings.push')}</Text>
        <Text style={styles.columnLabel}>{t('notificationSettings.inApp')}</Text>
      </View>

      {prefs.groups.map(({ group, types }) => {
        const expanded = !!open[group];
        const allOn = types.every((tp) => tp.push && tp.inApp);
        return (
          <View key={group} style={[styles.card, prefs.muted && styles.dimmed]}>
            <TouchableOpacity
              style={[styles.groupHeader, { flexDirection: rtl.rowDirection }]}
              onPress={() => setOpen((o) => ({ ...o, [group]: !o[group] }))}
              accessibilityRole="button"
              accessibilityState={{ expanded }}
            >
              <View style={styles.groupIcon}>
                <Ionicons name={GROUP_ICON[group] as any} size={18} color={colors.primary as string} />
              </View>
              <View style={styles.rowText}>
                <Text style={[styles.cardTitle, { textAlign: rtl.textStart }]}>{t(`notifications.group.${group}` as any)}</Text>
                <Text style={[styles.rowDescription, { textAlign: rtl.textStart }]}>
                  {t('notificationSettings.onCount', { on: types.filter((tp) => tp.push || tp.inApp).length, total: types.length })}
                </Text>
              </View>
              <Ionicons name={expanded ? 'chevron-up' : 'chevron-down'} size={18} color={colors.text.secondary as string} />
            </TouchableOpacity>

            {expanded && (
              <>
                <TouchableOpacity onPress={() => setGroup(group, !allOn)} disabled={prefs.muted} style={styles.groupAll}>
                  <Text style={[styles.groupAllText, { textAlign: rtl.isRTL ? 'left' : 'right' }]}>{t(allOn ? 'notificationSettings.turnAllOff' : 'notificationSettings.turnAllOn')}</Text>
                </TouchableOpacity>
                {types.map((tp) => {
                  const visual = notificationVisual(tp.key);
                  return (
                    <View key={tp.key} style={[styles.row, styles.typeRow, { flexDirection: rtl.rowDirection }]}>
                      <View style={[styles.typeIcon, { backgroundColor: withAlpha(visual.color, 0.12) }]}>
                        <Ionicons name={visual.icon as any} size={15} color={visual.color} />
                      </View>
                      <View style={styles.rowText}>
                        <Text style={[styles.rowTitle, { textAlign: rtl.textStart }]}>{t(`notificationSettings.type.${tp.key}.title` as any)}</Text>
                        <Text style={[styles.rowDescription, { textAlign: rtl.textStart }]}>{t(`notificationSettings.type.${tp.key}.description` as any)}</Text>
                      </View>
                      {toggle(tp.push, (v) => setType(tp.key, 'push', v), t('notificationSettings.push'), prefs.muted)}
                      {toggle(tp.inApp, (v) => setType(tp.key, 'inApp', v), t('notificationSettings.inApp'), prefs.muted)}
                    </View>
                  );
                })}
              </>
            )}
          </View>
        );
      })}
    </ScrollView>
  );
};

const getStyles = () => StyleSheet.create({
  container: { flex: 1, backgroundColor: colors.background.primary },
  content: { padding: spacing.md, gap: spacing.md, maxWidth: 760, width: '100%', alignSelf: 'center' },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: spacing.xl },
  muted: { color: colors.text.secondary, fontSize: typography.fontSize.sm },
  title: { fontSize: typography.fontSize.lg, fontFamily: typography.fontFamily.bold, color: colors.text.primary },
  subtitle: { fontSize: typography.fontSize.sm, color: colors.text.secondary, marginTop: -spacing.sm },
  card: { borderRadius: 14, borderWidth: 1, borderColor: colors.border, backgroundColor: colors.background.surface, padding: spacing.md, gap: spacing.xs },
  dimmed: { opacity: 0.5 },
  cardTitle: { fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.semibold, color: colors.text.primary },
  row: { alignItems: 'center', gap: spacing.sm, paddingVertical: spacing.xs },
  typeRow: { borderTopWidth: 1, borderTopColor: colors.border, paddingVertical: spacing.sm },
  rowText: { flex: 1, minWidth: 0 },
  rowTitle: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.medium, color: colors.text.primary },
  rowDescription: { fontSize: typography.fontSize.xs, color: colors.text.secondary, marginTop: 1 },
  toggleCell: { width: 56, alignItems: 'center' },
  testRow: { alignItems: 'center', gap: spacing.xs, paddingTop: spacing.sm, marginTop: spacing.xs, borderTopWidth: 1, borderTopColor: colors.border },
  testText: { fontSize: typography.fontSize.sm, color: colors.primary, fontFamily: typography.fontFamily.semibold },
  option: { alignItems: 'center', gap: spacing.sm, padding: spacing.sm, borderRadius: 10, borderWidth: 1, borderColor: 'transparent' },
  optionActive: { borderColor: colors.primary, backgroundColor: withAlpha(colors.primary as string, 0.06) },
  columnsHeader: { alignItems: 'center', gap: spacing.sm, paddingHorizontal: spacing.md, marginBottom: -spacing.sm },
  columnLabel: { width: 56, textAlign: 'center', fontSize: typography.fontSize.xs, fontFamily: typography.fontFamily.semibold, color: colors.text.secondary },
  groupHeader: { alignItems: 'center', gap: spacing.sm },
  groupIcon: { width: 34, height: 34, borderRadius: 17, alignItems: 'center', justifyContent: 'center', backgroundColor: withAlpha(colors.primary as string, 0.1) },
  groupAll: { paddingVertical: spacing.xs },
  groupAllText: { fontSize: typography.fontSize.xs, color: colors.primary, fontFamily: typography.fontFamily.semibold },
  typeIcon: { width: 28, height: 28, borderRadius: 14, alignItems: 'center', justifyContent: 'center' },
});
